import { t } from "i18next";
import { Component, ErrorInfo, ReactNode } from "react";

import { groupOrderItemsByPack } from "../../../../helpers";
import { OrderAtomProps } from "../OrderModal.types";
import {
  OrderCommission,
  OrderFeedback,
  OrderMargin,
  OrderPrice,
  OrderTrack
} from "./blocks";
import { OrderProduct } from "./OrderProduct";

import styles from "../OrderModal.module.scss";

class OrderRenderErrorBoundary extends Component<
  { children: ReactNode; orderId?: string },
  { error: Error | null }
> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error(
      "[OrderContent crash]",
      "\norderId:",
      this.props.orderId,
      "\nmessage:",
      error.message,
      "\nstack:",
      error.stack,
      "\ncomponentStack:",
      info.componentStack
    );
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 16, color: "#C80000", whiteSpace: "pre-wrap" }}>
          <strong>Render error:</strong> {this.state.error.message}
          {"\n"}
          {this.state.error.stack}
        </div>
      );
    }
    return this.props.children;
  }
}

const OrderContentInner = ({ orderDetails }: OrderAtomProps) => {
  if (!orderDetails) return <></>;

  // eslint-disable-next-line no-console
  console.log("[OrderContent] render", {
    orderId: orderDetails.id,
    itemCount: orderDetails.items?.length,
    items: orderDetails.items?.map((i) => ({
      id: i.id,
      barcodeId: i.barcodeId,
      hasBarcode: !!i.barcode,
      hasVariant: !!i.barcode?.productVariant,
      hasSize: !!i.barcode?.sizeAttributeValue,
      hasColor: !!i.barcode?.productVariant?.colorAttributeValue,
      packGroupId: i.packGroupId,
      packTitle: i.packTitle
    }))
  });

  const groupedItems = groupOrderItemsByPack(orderDetails.items);

  return (
    <>
      <OrderFeedback orderDetails={orderDetails} />

      <div className={styles.orderModal__content}>
        <div className={styles.orderModal__contentCol}>
          <OrderTrack orderDetails={orderDetails} />
          <div className={styles.orderModal__contentSeparator} />
          {orderDetails.totalMargin > 0 && (
            <>
              <OrderMargin orderDetails={orderDetails} />
              <div className={styles.orderModal__contentSeparator} />
            </>
          )}
          {orderDetails.totalCommission > 0 && (
            <>
              <OrderCommission orderDetails={orderDetails} />
              <div className={styles.orderModal__contentSeparator} />
            </>
          )}
          <OrderPrice orderDetails={orderDetails} />
        </div>
        <div className={styles.orderModal__contentCol}>
          <div className={styles.orderModal__productsBlock}>
            {groupedItems.map((entry, index) => {
              if (entry.kind === "standalone") {
                return <OrderProduct item={entry.item} key={entry.item.id} />;
              }

              const { group } = entry;
              return (
                <div
                  key={group.packGroupId}
                  className={styles.orderModal__productsBlock__packGroup}
                >
                  <div className={styles.orderModal__productsBlock__packHeader}>
                    {group.packTitle ?? `${t("Pack")} #${index + 1}`}
                    {group.packPrice != null && (
                      <span> · {group.packPrice.toLocaleString()} FCFA</span>
                    )}
                    {group.packIsAvailable === false && (
                      <span> · {t("Unavailable")}</span>
                    )}
                  </div>
                  {group.rows.map((row) => (
                    <OrderProduct item={row} key={row.id} />
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export const OrderContent = (props: OrderAtomProps) => (
  <OrderRenderErrorBoundary orderId={props.orderDetails?.id}>
    <OrderContentInner {...props} />
  </OrderRenderErrorBoundary>
);
